import {
  applyManualUnitPrice,
  canAdjustOrderLinePrice,
  clearHistoricalPromotionRoleForPricing,
  isHistoricalPromotionProvenanceLine,
  parseLinePriceAdjustment,
  parseManualUnitPrice,
  resolveOrderLineCharge
} from '@washday/utilities';
import { utils } from 'washday-sdk';
import type { IStore } from '../../../interfaces/Store';
import type { OrderProduct } from '../interfaces';
import type { OrderFormValues } from '../order.schema';
import { ensureLineIds, normalizeProductIdentity, normalizeSelectedOptionIdentity } from './lineIdentity';
import { calculateOrderTotalsFromFormValues } from './orderTotals';
import { recalculateOrderProductsWithLineIds } from './recalculateOrderProducts';

export type PriceAdjustment = { mode: 'unit'; unitPrice: number } | { mode: 'line'; extraAmount: number };
type PriceAdjustmentErrorReason = 'invalidUnitPrice' | 'invalidLineAdjustment' | 'priceOutOfRange' | 'couponMinimum' | 'previewUnavailable';

export class PriceAdjustmentError extends Error {
  constructor(readonly reason: PriceAdjustmentErrorReason) {
    super(reason);
  }
}

// Hydrated/cart lines have explicit IDs. Only old callers without a line ID may
// fall back to product + option, and only when that identifies a single line.
const findSelectedLineIndex = (products: OrderProduct[], product: OrderProduct): number => {
  const targetId = product.lineId || product.orderProductLineID;
  const matches = products.flatMap((line, index) => {
    const matchesTarget = targetId
      ? (line.lineId || line.orderProductLineID) === targetId
      : normalizeProductIdentity(line) === normalizeProductIdentity(product) &&
        normalizeSelectedOptionIdentity(line) === normalizeSelectedOptionIdentity(product);
    return matchesTarget ? [index] : [];
  });
  if (matches.length !== 1) throw new PriceAdjustmentError('previewUnavailable');
  return matches[0];
};

/** Web form adapter: preview and apply both use this existing recalculation path. */
export const previewPriceAdjustment = ({ order, product, store, storeDiscounts = [], adjustment }: {
  order: OrderFormValues;
  product: OrderProduct;
  store: IStore | null | undefined;
  storeDiscounts?: unknown[];
  adjustment: PriceAdjustment;
}) => {
  if (!store || !canAdjustOrderLinePrice(product)) throw new PriceAdjustmentError('previewUnavailable');
  const index = findSelectedLineIndex(order.products ?? [], product);
  const currentLine = order.products[index];
  if (!canAdjustOrderLinePrice(currentLine)) throw new PriceAdjustmentError('previewUnavailable');
  const line = { ...currentLine, qty: currentLine.quantity ?? currentLine.qty };
  const catalogCharge = resolveOrderLineCharge({ ...line, manualUnitPrice: null, extraAmount: 0 }, order.express);
  let candidate: typeof line;
  if (adjustment.mode === 'unit') {
    const unitPrice = parseManualUnitPrice(adjustment.unitPrice);
    if (unitPrice === null) throw new PriceAdjustmentError('invalidUnitPrice');
    const isCatalogPrice = Math.round(unitPrice * 100) === Math.round(catalogCharge.selectedUnitPrice * 100);
    try {
      candidate = applyManualUnitPrice({ ...line, manualUnitPrice: isCatalogPrice ? null : unitPrice, extraAmount: 0 }, order.express);
    } catch (error) {
      if (error instanceof RangeError) throw new PriceAdjustmentError('priceOutOfRange');
      throw error;
    }
  } else {
    const extraAmount = parseLinePriceAdjustment(adjustment.extraAmount);
    if (extraAmount === null) throw new PriceAdjustmentError('invalidLineAdjustment');
    candidate = { ...line, manualUnitPrice: null, extraAmount };
  }
  const candidateProducts = ensureLineIds(order.products.map((item, lineIndex) => lineIndex === index
    ? candidate
    : { ...item, qty: item.quantity ?? item.qty }));
  const lineId = candidateProducts[index].lineId;
  if (order.discountCodeObj && !utils.productsMeetMinimumSubtotal(
    [...candidateProducts, ...(order.buyAndGetProducts ?? [])
      .filter(isHistoricalPromotionProvenanceLine)
      .map(line => clearHistoricalPromotionRoleForPricing({ ...line, qty: line.quantity ?? line.qty }))],
    order.express, Number(order.discountCodeObj.minimumOrderSubtotal ?? 0), order
  )) throw new PriceAdjustmentError('couponMinimum');

  const { newOrderProds: products, buyAndGetProds: buyAndGetProducts } = recalculateOrderProductsWithLineIds({
    ...order, products: candidateProducts, storeDiscounts, previousBuyAndGetProducts: order.buyAndGetProducts ?? []
  });
  const calculated = calculateOrderTotalsFromFormValues({ ...order, products, buyAndGetProducts }, store, storeDiscounts);
  const lineTotals = calculated.lineDisplayTotals?.find(line => line.lineId === lineId);
  if (!lineTotals || ![calculated.total, lineTotals.subtotal, lineTotals.discount, lineTotals.taxesTotal, lineTotals.total].every(Number.isFinite)) {
    throw new PriceAdjustmentError('previewUnavailable');
  }
  return { products, buyAndGetProducts, lineTotals, orderTotal: calculated.total, catalogCharge };
};
